import { forwardRef, useLayoutEffect, useRef, type InputHTMLAttributes } from 'react'

/**
 * The canonical phone-number field for the three DRIVER surfaces — checkout's
 * identify step, /signin, and the account pane's phone-change flow. (The ops,
 * tenant-portal and enforcement-portal phone inputs are still raw and are
 * deliberately not in scope here.) It lives here for the same reason `OtpInput`
 * does, and #1715 records that reasoning in full:
 * `packages/account` is the only shared package both hosts already load a
 * stylesheet from, and one of the three call sites is inside this package.
 *
 * It formats as you type — `(704) 555-0146` — which production did not do at
 * any of the three sites; each one passed the raw value straight through.
 *
 * The behaviour here is load-bearing. Read before simplifying:
 *
 *  - THE CARET IS TRACKED BY DIGIT INDEX, NEVER BY CHARACTER OFFSET. Formatting
 *    inserts punctuation as you type: the 4th digit turns `(704` into `(704) `,
 *    moving every later character by two. What survives that is "how many digits
 *    precede the caret", so the caret is restored after the n-th digit of the
 *    reformatted string.
 *  - THE RESTORE IS A LAYOUT EFFECT WITH NO DEPENDENCY ARRAY. The caret has to
 *    be put back in the same frame the browser moved it, or the field visibly
 *    flickers to the end and back. `useEffect` is not equivalent here.
 *  - `setSelectionRange` IS WRAPPED. It throws on input types that do not
 *    support selection, and a future call site passing `type="number"` must not
 *    take the surface down with it.
 *  - A `+` PREFIX PASSES THROUGH UNMASKED, AND ITS CARET IS LEFT ALONE.
 *    `normalizePhoneE164` accepts a `+`-prefixed 8–15-digit number and hands it
 *    to Twilio Verify, on the client and in the Worker. A US-only mask that
 *    strips `+` and caps at ten digits would make an international parker unable
 *    to sign in or check out. The demo this is ported from records that limit as
 *    accepted rather than solved (`platephone-test.js`) — defensible for a sales
 *    demo, not for a live paying surface. Restoring a digit-index caret on this
 *    branch is just as wrong: it lands in front of the `+`.
 *
 * No `maxLength`, deliberately, and no `onPaste` handler: a hard cap truncates a
 * paste BEFORE onChange fires (the trap `OtpInput` documents), and the leading
 * country-code rule below means the formatter alone handles a pasted
 * `1 (704) 555-0146` correctly.
 */

/** Digits the mask keeps for a US number. */
const US_DIGITS = 10

/**
 * Progressive US mask, with the two shapes `normalizePhoneE164` also accepts
 * left intact:
 *
 *  - a `+` prefix is returned untouched — international, not ours to reshape;
 *  - eleven digits beginning `1` drop the country code before masking, so a
 *    pasted or typed `1 704 555 0146` becomes `(704) 555-0146` rather than the
 *    `(170) 455-5014` a bare ten-digit slice produces.
 */
export type PhoneOverflow = 'truncate' | 'keep'

/**
 * `overflow` decides what happens to digits beyond the ten the US mask keeps:
 *  - `'truncate'` (default, the checkout contract): an eleventh digit typed
 *    into a full field is ignored, as every test in consumer/tests pins.
 *  - `'keep'`: the digits are returned bare, unmasked, so a validator can SEE
 *    them. A pasted `404555014222` would otherwise become `(404) 555-0142` —
 *    a valid-looking, different real phone number — before any rule ran.
 *    The parker forms (tenant, ops, enforcement) use this: their stored value
 *    is exactly ten digits and a silent twelve-to-ten cut saves someone
 *    else's number (review finding on #3634).
 */
export function formatPhoneAsTyped(raw: string, overflow: PhoneOverflow = 'truncate'): string {
  if (raw.trimStart().startsWith('+')) return raw
  // A `+` that arrived anywhere else. Recovered rather than deleted: the
  // pass-through above is a PREFIX test re-evaluated on every keystroke, so a
  // `+` reaching the field second was destroyed by the digit strip below and
  // the number was silently masked and truncated as US — a different real phone
  // number, and no way back without clearing the field. On iOS the `+` sits
  // behind a long press on the `+*#` key, so landing it second is ordinary
  // rather than exotic: it is how a parker at sandbox #75 lost `+44`.
  //
  // Normalised to `+` and digits, which is the shape `normalizePhoneE164`
  // wants. The prefix branch above is left byte-identical, so the path that
  // already worked does not move.
  if (raw.includes('+')) {
    const intl = raw.replace(/\D/g, '')
    return intl ? `+${intl}` : '+'
  }
  let digits = raw.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  if (overflow === 'keep' && digits.length > US_DIGITS) return digits
  digits = digits.slice(0, US_DIGITS)
  if (!digits) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/**
 * Where the caret belongs in `formatted` when `digitsBefore` digits should sit
 * to its left. Returns the offset just past the n-th digit, so the caret lands
 * where the user's last keystroke did regardless of what punctuation moved.
 */
export function phoneCaretAfter(formatted: string, digitsBefore: number): number {
  if (digitsBefore <= 0) return 0
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    const c = formatted.charCodeAt(i)
    if (c >= 48 && c <= 57 && ++seen === digitsBefore) return i + 1
  }
  return formatted.length
}

export interface PhoneInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> {
  /** The formatted value. The parent stores exactly what is rendered. */
  value: string
  /** Receives the formatted value, matching OtpInput and ValidationPinInput. */
  onChange: (value: string) => void
  /** What happens to digits past the ten the mask keeps. See `formatPhoneAsTyped`. */
  overflow?: PhoneOverflow
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onChange, overflow = 'truncate', ...rest },
  ref,
) {
  const own = useRef<HTMLInputElement | null>(null)
  // Where the caret must end up after React rewrites the value. Null when the
  // change did not come from typing, so a re-render for any other reason leaves
  // the caret alone.
  const caret = useRef<number | null>(null)

  useLayoutEffect(() => {
    const el = own.current
    if (!el || caret.current === null) return
    const at = Math.min(caret.current, el.value.length)
    caret.current = null
    try {
      el.setSelectionRange(at, at)
    } catch {
      // Input types without selection support throw here. Losing the caret
      // position is survivable; taking down the checkout is not.
    }
  })

  return (
    <input
      type="tel"
      // `tel`, not `numeric`: the keypad must keep `+` reachable for an
      // international number, which this field still accepts.
      inputMode="tel"
      autoComplete="tel"
      {...rest}
      ref={(el) => {
        own.current = el
        if (typeof ref === 'function') ref(el)
        else if (ref) ref.current = el
      }}
      value={value}
      onChange={(e) => {
        const raw = e.target.value
        const at = e.target.selectionStart ?? raw.length
        const formatted = formatPhoneAsTyped(raw, overflow)

        // International: nothing was reshaped, so the browser's own caret is
        // already correct and MUST be left alone. Restoring a digit-index caret
        // here put it in front of the `+`, after which every later keystroke
        // landed before it, the `+` test stopped matching, and the US mask took
        // over — `+447700900123` became `+14477009001`, a different real phone.
        if (formatted === raw && raw.trimStart().startsWith('+')) {
          caret.current = null
          onChange(formatted)
          return
        }

        // A `+` recovered from mid-string. The value DID change, so the caret
        // has to be placed — by digit index, which works here because the
        // normalised form is `+` followed by digits and nothing else. (The
        // branch above cannot use this: it preserves the raw string, spaces and
        // all, where a digit index lands in front of the `+`.)
        if (formatted.startsWith('+')) {
          const before = (raw.slice(0, at).match(/\d/g) ?? []).length
          caret.current = Math.min(before + 1, formatted.length)
          onChange(formatted)
          return
        }

        let digitsBefore = (raw.slice(0, at).match(/\d/g) ?? []).length
        // If the mask is about to drop a leading country code, the caret has one
        // fewer digit to its left than the raw value suggests.
        const allDigits = raw.replace(/\D/g, '')
        if (allDigits.length === 11 && allDigits.startsWith('1') && digitsBefore > 0) {
          digitsBefore -= 1
        }
        const target = phoneCaretAfter(formatted, digitsBefore)

        // FORWARD DELETE ACROSS MASK PUNCTUATION.
        //
        // Put the caret immediately before the hyphen in `(704) 555-0146` and
        // press Delete. The browser removes the hyphen; the formatter puts it
        // straight back; `formatted === value`, so the branch below restores the
        // caret to exactly where it was. Every subsequent press repeats it and
        // the field cannot be edited forward at all. The same trap sits before
        // `(` at offset 0 and before `)` at offset 4.
        //
        // The mask's punctuation is not the parker's content — the digits are.
        // So a Delete aimed at punctuation deletes the DIGIT BEYOND IT rather
        // than skipping and doing nothing. One press, one deletion, which is
        // what the parker means; skipping instead would spend a keystroke
        // moving an invisible caret and read as the field ignoring them.
        //
        // Keyed on `inputType` because that is the only thing separating this
        // from an insertion that happened to reformat to the same string.
        const forwardDelete =
          (e.nativeEvent as InputEvent | undefined)?.inputType === 'deleteContentForward'
        if (forwardDelete && formatted === value) {
          const digits = value.replace(/\D/g, '')
          // No digit beyond the caret (it is at the end) — nothing to delete,
          // and falling through would restore the caret, which is correct.
          if (digitsBefore < digits.length) {
            const next = formatPhoneAsTyped(
              digits.slice(0, digitsBefore) + digits.slice(digitsBefore + 1),
              overflow,
            )
            // `next` differs from `value`, so the parent re-renders and the
            // layout effect places the caret. It stays at the same digit index,
            // so a held Delete walks forward one digit per press.
            caret.current = phoneCaretAfter(next, digitsBefore)
            onChange(next)
            return
          }
        }

        // BACKSPACE ACROSS MASK PUNCTUATION — the mirror of the branch above,
        // and the same trap from the other side. Put the caret just after the
        // hyphen in `(704) 555-0146` and press Backspace: the browser removes
        // the hyphen, the formatter puts it straight back, and the value is
        // byte-identical, so the press does nothing. The parker has to hit
        // Backspace twice at every `(`, `)`, space and `-`, with nothing on
        // screen explaining why the first press was ignored.
        //
        // Found on a real iPhone (sandbox #30): "it tries to backspace the `-`,
        // doesn't actually, and leaves the number intact, with the caret going
        // back to 5." Every part behaves as written; the composition eats the
        // keystroke.
        //
        // So a Backspace aimed at punctuation deletes the DIGIT BEFORE IT, which
        // is what the parker means. Keyed on `inputType` for the same reason the
        // forward case is: nothing else separates it from an insertion that
        // happened to reformat to the same string.
        const backspace =
          (e.nativeEvent as InputEvent | undefined)?.inputType === 'deleteContentBackward'
        if (backspace && formatted === value && digitsBefore > 0) {
          const digits = value.replace(/\D/g, '')
          const next = formatPhoneAsTyped(
            digits.slice(0, digitsBefore - 1) + digits.slice(digitsBefore),
            overflow,
          )
          caret.current = phoneCaretAfter(next, digitsBefore - 1)
          onChange(next)
          return
        }

        if (formatted === value) {
          // The keystroke changed nothing the parent stores — a non-digit, or a
          // digit past the cap. React bails out of the re-render, so the layout
          // effect never runs; arming it here would leave a stale offset to fire
          // on the next unrelated render (a Turnstile token arriving, `busy`
          // flipping) and jump the caret mid-typing. React still rewrites the DOM
          // value, which parks the caret at the end, so put it back after that.
          caret.current = null
          queueMicrotask(() => {
            const el = own.current
            if (!el) return
            try {
              el.setSelectionRange(target, target)
            } catch {
              // see the layout effect
            }
          })
          onChange(formatted)
          return
        }

        caret.current = target
        onChange(formatted)
      }}
    />
  )
})
